import React, { useEffect } from 'react'
import clsx from 'clsx'
import { useFormContext } from 'react-hook-form'
import {
  RespondentGroupsQuestion as RespondentGroupsQuestionType,
  RespondentInfo,
  RespondentGroup,
} from '../../../../types/end-journey'
import { BaseQuestionLayout } from '../BaseQuestionLayout'
import {
  PlusIcon,
  MinusCircleIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline'
import { Tooltip } from '@chakra-ui/react'

interface RespondentGroupsQuestionProps {
  name: string
  question: RespondentGroupsQuestionType
  groups: RespondentGroup[]
}

export const RespondentGroupsQuestion: React.FC<
  RespondentGroupsQuestionProps
> = ({ name, question, groups }) => {
  const {
    register,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext()

  const formName = name + '_other'
  const values = watch(formName) || {}

  // Initialize form with server data and set up email extraction
  useEffect(() => {
    // Initialize form with server data
    groups.forEach((group) => {
      if (group.emails.length > 0) {
        setValue(
          `${formName}.${group.type}`,
          group.emails.map((emailObj) => ({
            email: emailObj.email,
            hasCompleted: emailObj.hasCompleted,
          }))
        )
      }
    })
  }, [groups, name, setValue, watch, getValues])

  const handleAddEmail = (groupType: 'manager' | 'peer' | 'reportee') => {
    const currentEmails = values[`${groupType}`] || []
    setValue(`${formName}.${groupType}`, [
      ...currentEmails,
      { email: '', hasCompleted: false },
    ])
  }

  const handleRemoveEmail = (
    groupType: 'manager' | 'peer' | 'reportee',
    emailIndex: number
  ) => {
    const currentEmails = values[`${groupType}`] || []
    setValue(
      `${formName}.${groupType}`,
      currentEmails.filter((_: RespondentInfo, i: number) => i !== emailIndex)
    )
  }

  const error = errors[formName]?.message as string | undefined

  return (
    <BaseQuestionLayout question={question} error={error}>
      <div className="space-y-12">
        {groups.map((group, groupIndex) => (
          <div key={`${group.type}-${groupIndex}`} className="space-y-2">
            <h3 className="text-gray-900 font-medium">{group.title}</h3>
            {group.description && (
              <p className="text-gray-600 text-sm">{group.description}</p>
            )}

            <div className="space-y-2">
              {(values[`${group.type}`] || []).map(
                (emailObj: RespondentInfo, emailIndex: number) => (
                  <div key={emailIndex} className="flex items-center gap-2">
                    <div className="relative flex-grow">
                      <input
                        type="email"
                        readOnly={emailObj.hasCompleted}
                        className={clsx(
                          'w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-1 focus:ring-[#2D7BE8] focus:border-[#2D7BE8]',
                          'read-only:bg-gray-100 read-only:text-gray-600',
                          errors[
                            `${formName}.${group.type}.${emailIndex}.email`
                          ]
                            ? 'border-red-500'
                            : 'border-gray-300'
                        )}
                        placeholder="email@company.com"
                        {...register(
                          `${formName}.${group.type}.${emailIndex}.email`,
                          {
                            required: 'Email is required',
                            pattern: {
                              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                              message: 'Invalid email address',
                            },
                          }
                        )}
                      />
                      {/* Completed indicator with tooltip - only show for completed emails */}
                      {emailObj.hasCompleted && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <Tooltip
                            label="Has completed your Start of Journey Hupo Perspectives"
                            placement="top"
                            hasArrow
                          >
                            <CheckCircleIcon className="w-5 h-5 text-green-500 cursor-help" />
                          </Tooltip>
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveEmail(group.type, emailIndex)}
                      className="p-2 text-gray-400 hover:text-gray-600"
                    >
                      <MinusCircleIcon className="w-5 h-5" />
                    </button>
                  </div>
                )
              )}
            </div>

            <button
              type="button"
              onClick={() => handleAddEmail(group.type)}
              className="flex items-center gap-2 text-[#2D7BE8] hover:text-[#1B5BB5] font-medium"
            >
              <PlusIcon className="w-5 h-5" />
              <span>Add more</span>
            </button>
          </div>
        ))}
      </div>
    </BaseQuestionLayout>
  )
}
